import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@/spa/routerCompat";
import { useWorkspaceChrome } from "@/spa/workspaceChromeContext";
import type { LayoutUser, SidebarProjectSummary } from "@/ui/layoutUser";
import type { ProjectDetailBootstrap } from "./client/ProjectDetailPageClient";
import { ProjectDetailInteractiveMount } from "./client/ProjectDetailInteractiveMount";
import { ProjectDeploymentsPanel } from "./client/ProjectDeploymentsPanel";
import { ProjectSiteGlyph } from "@/ui/client/ProjectSiteGlyph";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { ScrollText, Settings } from "lucide-react";
import { pickFeaturedDeploymentFromSortedDesc } from "@/lib/sidebarFeaturedDeployment";
import { cn } from "@/lib/utils";

export type DeploymentTrigger =
  | "manual"
  | "github_push"
  | "deploy_hook"
  | "pr_preview"
  | "runtime_recovery";

type Deployment = {
  id: string;
  shortId: string;
  projectId: string;
  artifactPrefix: string;
  status: string;
  serveStrategy: "static" | "server";
  buildPreviewMode: "auto" | "static" | "server" | null;
  buildLogKey: string | null;
  trigger: DeploymentTrigger;
  sourceBranch: string | null;
  sourceCommitSha: string | null;
  previewUrl: string | null;
  createdAt: string;
  finishedAt: string | null;
};

type Project = {
  id: string;
  name: string;
  repoUrl: string;
  branch: string;
  workspaceRootDir: string;
  projectRootDir: string;
  frameworkHint: "auto" | "nextjs" | "node" | "python" | "static";
  previewMode: "auto" | "static" | "server";
  serverPreviewTarget: "isolated-runner";
  runtimeImageMode: "auto" | "platform" | "dockerfile";
  dockerfilePath: string | null;
  dockerBuildTarget: string | null;
  skipHostStrategyBuild: boolean;
  runtimeContainerPort: number;
  installCommand: string | null;
  buildCommand: string | null;
  createdAt: string;
  updatedAt: string;
  currentDeploymentId: string | null;
  previousDeploymentId: string | null;
  siteIconUrl: string | null;
  siteOgImageUrl: string | null;
  siteMetaFetchedAt: string | null;
  siteMetaError: string | null;
  runtimeHealth?: "ok" | "recovering" | "image_missing" | "ensure_failed" | null;
  runtimeHealthDetail?: string | null;
  runtimeHealthCheckedAt?: string | null;
  runtimeAutoRebuildAt?: string | null;
};

export type ProjectDetailData = {
  pathname: string;
  projectRole: "owner" | "editor" | "viewer";
  project: Project;
  deployments: Deployment[];
  currentPreviewUrl: string | null;
  runtimeLogsAvailable: boolean;
  user?: LayoutUser | null;
  csrfToken: string;
  sidebarProjects: SidebarProjectSummary[];
};

const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case "success": return "default";
    case "failed": return "destructive";
    case "building": return "outline";
    case "queued": return "secondary";
    default: return "secondary";
  }
};

export const ProjectDetailPage = ({
  data,
  onRequestDetailRefetch
}: {
  data: ProjectDetailData;
  onRequestDetailRefetch?: () => void;
}) => {
  const { t } = useTranslation();
  const deploymentPreviewLabel = (deployment: Deployment): string => {
    if (deployment.buildPreviewMode === "server") return t("deployment.previewMode.server");
    if (deployment.buildPreviewMode === "static") return t("deployment.previewMode.static");
    return deployment.serveStrategy === "server"
      ? t("deployment.previewMode.server")
      : t("deployment.previewMode.static");
  };

  const deploymentStatusLabel = (s: string): string => {
    const k = s.toLowerCase();
    if (k === "building") return t("deployment.status.building");
    if (k === "queued") return t("deployment.status.queued");
    if (k === "success") return t("deployment.status.success");
    if (k === "failed") return t("deployment.status.failed");
    return s;
  };

  const deployments = data.deployments ?? [];

  const currentDeployment =
    data.project.currentDeploymentId !== null
      ? deployments.find((d) => d.id === data.project.currentDeploymentId) ?? null
      : null;

  const deploymentStatusesKey = useMemo(
    () => data.deployments.map((d) => `${d.id}:${d.status}`).join("|"),
    [data.deployments]
  );

  const interactiveBootstrap = useMemo((): ProjectDetailBootstrap => {
    const rows = data.deployments ?? [];
    return {
      projectId: data.project.id,
      projectName: data.project.name,
      repoUrl: data.project.repoUrl,
      branch: data.project.branch,
      projectRootDir: data.project.projectRootDir,
      currentPreviewUrl: data.currentPreviewUrl,
      hasSuccessfulDeployment: rows.some((d) => d.status === "success"),
      siteMeta:
        data.currentPreviewUrl !== null
          ? {
              siteIconUrl: data.project.siteIconUrl,
              siteOgImageUrl: data.project.siteOgImageUrl,
              siteMetaFetchedAt: data.project.siteMetaFetchedAt,
              siteMetaError: data.project.siteMetaError
            }
          : null,
      currentDeploymentId: data.project.currentDeploymentId,
      previousDeploymentId: data.project.previousDeploymentId,
      projectRole: data.projectRole
    };
  }, [
    data.project.id,
    data.project.name,
    data.project.repoUrl,
    data.project.branch,
    data.project.projectRootDir,
    data.currentPreviewUrl,
    data.project.siteIconUrl,
    data.project.siteOgImageUrl,
    data.project.siteMetaFetchedAt,
    data.project.siteMetaError,
    data.project.currentDeploymentId,
    data.project.previousDeploymentId,
    data.projectRole,
    deploymentStatusesKey
  ]);

  const chrome = useMemo(
    () => ({
      title: t("meta.projectTitle", { name: data.project.name, appName: t("common.appName") }),
      breadcrumbs: [
        { label: t("common.projects"), href: "/projects" },
        { label: data.project.name }
      ],
      sidebarContext: {
        project: {
          id: data.project.id,
          name: data.project.name,
          siteIconUrl: data.project.siteIconUrl,
          previewUrl: data.currentPreviewUrl
        },
        deployment: pickFeaturedDeploymentFromSortedDesc(
          deployments.map((d) => ({
            id: d.id,
            shortId: d.shortId,
            status: d.status
          }))
        )
      }
    }),
    [t, data.project.id, data.project.name, data.project.siteIconUrl, data.currentPreviewUrl, deploymentStatusesKey]
  );
  useWorkspaceChrome(chrome);

  const runtimeHealth = data.project.runtimeHealth;
  const showRuntimeBanner =
    runtimeHealth === "image_missing" ||
    runtimeHealth === "ensure_failed" ||
    runtimeHealth === "recovering";

  return (
    <>
    {showRuntimeBanner ? (
      <div
        className={cn(
          "mb-4 rounded-lg border px-4 py-3 text-sm",
          runtimeHealth === "recovering"
            ? "border-amber-500/40 bg-amber-500/10 text-amber-950 dark:text-amber-100"
            : "border-destructive/40 bg-destructive/10 text-destructive"
        )}
        role="status"
      >
        <p className="font-medium">
          {runtimeHealth === "recovering"
            ? t("projectDetail.runtimeRecoveringTitle")
            : runtimeHealth === "image_missing"
              ? t("projectDetail.runtimeImageMissingTitle")
              : t("projectDetail.runtimeEnsureFailedTitle")}
        </p>
        <p className="mt-1 text-sm opacity-90">
          {data.project.runtimeHealthDetail?.trim() ||
            (runtimeHealth === "recovering"
              ? t("projectDetail.runtimeRecoveringBody")
              : runtimeHealth === "image_missing"
                ? t("projectDetail.runtimeImageMissingBody")
                : t("projectDetail.runtimeEnsureFailedBody"))}
        </p>
        {runtimeHealth === "image_missing" || runtimeHealth === "ensure_failed" ? (
          <p className="mt-2 text-sm opacity-90">{t("projectDetail.runtimeRebuildHint")}</p>
        ) : null}
      </div>
    ) : null}
    <div className="relative mb-8 overflow-hidden rounded-lg border border-border/80 border-l-4 border-l-primary/70 bg-card/30 shadow-sm ring-1 ring-black/5 dark:ring-white/5">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-r from-primary/40 via-transparent to-transparent"
        aria-hidden
      />
      <div className="dashboard-surface relative flex flex-col gap-6 overflow-hidden rounded-none border-0 shadow-none p-5 lg:flex-row lg:items-stretch lg:gap-8 md:p-6">
        {data.currentPreviewUrl ? (
          <div className="w-full shrink-0 lg:max-w-[min(100%,440px)] xl:max-w-[min(100%,480px)]">
            <div className="aspect-16/10 w-full">
              <div id="project-detail-hero-preview-root" className="size-full min-w-0" />
            </div>
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-5">
          <div className="min-w-0 space-y-4">
            <div>
              <p className="eyebrow-label mb-2">
                {currentDeployment ? t("projectDetail.currentDeployment") : t("projectDetail.projectEyebrow")}
              </p>
              <div className="flex min-w-0 items-center gap-3">
                <ProjectSiteGlyph
                  name={data.project.name}
                  siteIconUrl={data.project.siteIconUrl}
                  previewUrl={data.currentPreviewUrl}
                  className="size-6 shrink-0 ring-0"
                  imgClassName="size-6 shrink-0 rounded-md object-cover"
                  letterClassName="flex size-6 items-center justify-center rounded-md bg-primary/20 text-xs font-semibold text-primary"
                />
                <h1 className="font-serif min-w-0 flex-1 truncate text-3xl font-semibold tracking-tight md:text-4xl">
                  {data.project.name}
                </h1>
              </div>
              <p className="mt-2 truncate font-mono text-sm text-muted-foreground">
                {data.project.repoUrl.replace(/^https:\/\/github\.com\//, "")}
              </p>
            </div>
            <dl className="grid gap-4 text-sm md:text-base">
              {data.currentPreviewUrl ? (
                <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
                  <dt className="text-muted-foreground">{t("projectDetail.previewLabel")}</dt>
                  <dd className="min-w-0 truncate font-mono text-sm">
                    <a
                      href={data.currentPreviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-foreground font-medium no-underline hover:underline"
                    >
                      {data.currentPreviewUrl}
                    </a>
                  </dd>
                </div>
              ) : null}
              <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">{t("projectDetail.branch")}</dt>
                <dd className="min-w-0 truncate font-mono text-sm">
                  <code>{data.project.branch}</code>
                </dd>
              </div>
              {currentDeployment ? (
                <>
                  <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1">
                    <dt className="text-muted-foreground">{t("common.deployment")}</dt>
                    <dd className="min-w-0">
                      <Link
                        to={`/deployments/${currentDeployment.id}`}
                        className="font-mono text-sm font-medium no-underline hover:underline"
                      >
                        {currentDeployment.shortId}
                      </Link>
                    </dd>
                  </div>
                  <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1">
                    <dt className="text-muted-foreground">{t("projectDetail.status")}</dt>
                    <dd className="flex flex-wrap items-center gap-2">
                      <Badge variant={statusVariant(currentDeployment.status)}>
                        {deploymentStatusLabel(currentDeployment.status)}
                      </Badge>
                      <Badge variant="outline" className="font-normal">
                        {deploymentPreviewLabel(currentDeployment)}
                      </Badge>
                    </dd>
                  </div>
                  <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
                    <dt className="text-muted-foreground">{t("projectDetail.created")}</dt>
                    <dd className="text-sm tabular-nums text-muted-foreground">
                      {new Date(currentDeployment.createdAt).toLocaleString()}
                    </dd>
                  </div>
                </>
              ) : null}
            </dl>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={data.projectRole === "viewer" ? `/projects/${data.project.id}/settings/members` : `/projects/${data.project.id}/settings`} aria-label={t("projectDetail.settingsAria")}>
                <Settings className="mr-1.5 size-4" aria-hidden />
                {t("projectDetail.settings")}
              </Link>
            </Button>
            {data.runtimeLogsAvailable &&
            currentDeployment?.serveStrategy === "server" &&
            currentDeployment.status === "success" ? (
              <Button variant="outline" size="sm" asChild>
                <Link
                  to={`/projects/${data.project.id}/observability#runtime-logs`}
                  aria-label={t("projectDetail.runtimeLogsAria")}
                >
                  <ScrollText className="mr-1.5 size-4" aria-hidden />
                  {t("projectDetail.runtimeLogs")}
                </Link>
              </Button>
            ) : null}
            {data.currentPreviewUrl ? (
              <Button variant="outline" asChild>
                <a href={data.currentPreviewUrl} target="_blank" rel="noopener noreferrer">
                  {t("projectDetail.visit")}
                </a>
              </Button>
            ) : null}
            {data.projectRole !== "viewer" ? (
              <>
                <div
                  className="flex w-full min-w-0 max-w-md flex-col gap-2 sm:max-w-none sm:flex-1 sm:basis-full lg:basis-auto lg:max-w-md"
                  id="project-detail-deploy-main-root"
                />
                <div id="project-detail-set-current-root" />
                <div id="project-detail-rollback-root" />
              </>
            ) : null}
          </div>
        </div>
      </div>

      <Accordion type="single" collapsible className="border-t border-border/60 bg-muted/15 px-5 md:px-6">
        <AccordionItem value="project-details" className="border-0">
          <AccordionTrigger className="py-4 text-base font-semibold hover:no-underline md:text-lg">
            {t("projectDetail.accordionTitle")}
          </AccordionTrigger>
          <AccordionContent className="space-y-6 pb-6">
            <p className="text-sm leading-relaxed text-muted-foreground">{t("projectDetail.accordionHint")}</p>
            {data.projectRole !== "viewer" ? <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <Button variant="default" size="sm" className="justify-center gap-2 sm:justify-start" asChild>
                <Link to={`/projects/${data.project.id}/settings`}>
                  <Settings className="size-4" aria-hidden />
                  {t("projectDetail.editGeneral")}
                </Link>
              </Button>
              <Button variant="outline" size="sm" className="justify-center sm:justify-start" asChild>
                <Link to={`/projects/${data.project.id}/settings/env`}>{t("projectDetail.editEnv")}</Link>
              </Button>
            </div> : null}
            <div className="overflow-hidden rounded-xl border border-border/70 shadow-sm">
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.tableRepo")}</TableCell>
                    <TableCell className="py-3 text-sm">
                      <a
                        href={data.project.repoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="no-underline hover:underline"
                      >
                        {data.project.repoUrl.replace("https://github.com/", "")}
                      </a>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.workspaceRoot")}</TableCell>
                    <TableCell className="py-3 text-sm">
                      <code>{data.project.workspaceRootDir}</code>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.projectRoot")}</TableCell>
                    <TableCell className="py-3 text-sm">
                      <code>{data.project.projectRootDir}</code>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.framework")}</TableCell>
                    <TableCell className="py-3 text-sm">
                      {data.project.frameworkHint === "auto" ? t("projectDetail.autoDetect") : data.project.frameworkHint}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.previewType")}</TableCell>
                    <TableCell className="py-3 text-sm capitalize">
                      {data.project.previewMode === "auto" ? t("projectDetail.autoDetect") : data.project.previewMode}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.serverTarget")}</TableCell>
                    <TableCell className="py-3 text-sm">{t("projectDetail.isolatedRunner")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.runtimeImage")}</TableCell>
                    <TableCell className="py-3 text-sm capitalize">{data.project.runtimeImageMode}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.dockerfile")}</TableCell>
                    <TableCell className="py-3 text-sm">
                      <code>{data.project.dockerfilePath ?? "Dockerfile"}</code>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.dockerTarget")}</TableCell>
                    <TableCell className="py-3 text-sm">{data.project.dockerBuildTarget ?? t("common.emDash")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.hostBuild")}</TableCell>
                    <TableCell className="py-3 text-sm">
                      {data.project.skipHostStrategyBuild
                        ? t("projectDetail.skippedDockerfile")
                        : t("projectDetail.runStrategyBuild")}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.created")}</TableCell>
                    <TableCell className="py-3 text-sm">{new Date(data.project.createdAt).toLocaleString()}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="w-40 py-3 text-sm font-medium text-muted-foreground">{t("projectDetail.updated")}</TableCell>
                    <TableCell className="py-3 text-sm">{new Date(data.project.updatedAt).toLocaleString()}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>

    <div className="min-w-0 space-y-8">
      <Card className="dashboard-surface overflow-hidden border-border/80 shadow-none">
        <CardHeader className="space-y-1 px-5 pb-4 pt-6 md:px-6">
          <CardTitle className="text-xl font-semibold tracking-tight md:text-2xl">{t("projectDetail.deploymentCardTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {deployments.length === 0 ? (
            <p className="text-muted-foreground px-6 pb-6 pt-2 text-base">{t("projectDetail.noDeployments")}</p>
          ) : (
            <div className="min-w-0">
              <ProjectDeploymentsPanel
                deployments={deployments.map((d) => ({
                  id: d.id,
                  shortId: d.shortId,
                  status: d.status,
                  serveStrategy: d.serveStrategy,
                  buildPreviewMode: d.buildPreviewMode,
                  trigger: d.trigger,
                  sourceBranch: d.sourceBranch,
                  sourceCommitSha: d.sourceCommitSha,
                  previewUrl: d.previewUrl,
                  createdAt: d.createdAt
                }))}
                currentDeploymentId={data.project.currentDeploymentId}
                canMutate={data.projectRole !== "viewer"}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="dashboard-surface overflow-hidden border-border/80 shadow-none">
        <CardHeader className="space-y-2 px-5 pb-3 pt-6 md:px-6">
          <CardTitle className="text-xl font-semibold tracking-tight md:text-2xl">{t("repoExplorer.defaultTitle")}</CardTitle>
          <CardDescription className="max-w-3xl text-base leading-relaxed">{t("repoExplorer.intro")}</CardDescription>
        </CardHeader>
        <CardContent className="px-4 pb-6 pt-0 md:px-6">
          <div id="project-detail-repo-explorer-root" className="min-w-0" />
        </CardContent>
      </Card>
    </div>
    <ProjectDetailInteractiveMount bootstrap={interactiveBootstrap} onRequestDetailRefetch={onRequestDetailRefetch} />
    </>
  );
};
